import { api } from "./client";
import type { WorkTaskRelatedEntityType } from "@/api/workTasks";

/**
 * Calendar Appointments v1.0 — frontend client. Mirrors the backend
 * <c>CalendarAppointment</c> aggregate and its DTOs
 * (<c>Core/DTOs/Appointments/AppointmentDtos.cs</c>). A near-clone of the
 * Tasks module client; the related-entity link reuses the shared
 * <c>WorkTaskRelatedEntityType</c> union (imported, never redefined).
 */

/** Lifecycle status — mirrors <c>AppointmentStatuses</c>. */
export type AppointmentStatus = "Scheduled" | "Cancelled" | "Completed";

/** An invited attendee's response — mirrors <c>AppointmentAttendeeResponses</c>. */
export type AppointmentAttendeeResponse = "Invited" | "Accepted" | "Declined";

export interface AppointmentAttendeeDto {
  userId: string;
  displayName: string;
  response: AppointmentAttendeeResponse;
}

/** An external (non-staff) attendee — emailed on create, never an internal user. */
export interface ExternalAttendeeDto {
  name: string;
  email: string;
  notes?: string | null;
  /**
   * The attendee's own IANA zone, so their invitation can show them their own
   * hour. Constrained server-side to the zones the platform carries a NAME for.
   *
   * Three states on the wire: absent/null leaves a stored value alone, `""`
   * clears it, an id sets it. The form hydrates and always sends this, so the
   * picker can both set and clear; a caller that omits it cannot destroy a value
   * it does not know about.
   */
  timeZone?: string | null;
}

export interface AppointmentRelatedEntitySummaryDto {
  type: WorkTaskRelatedEntityType;
  id: string;
  displayName: string;
  detailUrl: string | null;
}

/** Mirrors `RecurrenceFrequency` (int-stored server-side; sent as the name). */
export type RecurrenceFrequency = "Daily" | "Weekly" | "Monthly" | "Yearly";

/** Mirrors `RecurrenceMonthlyMode`. */
export type RecurrenceMonthlyMode =
  | "ByDayOfMonth"
  | "ByWeekdayPosition"
  | "LastDayOfMonth";

/** Mirrors `RecurrenceEndMode`. */
export type RecurrenceEndMode = "Never" | "AfterOccurrences" | "OnDate";

/** Mirrors `RecurrenceEditScope`. Sent on update and cancel of a series occurrence. */
export type RecurrenceEditScope = "ThisOccurrence" | "ThisAndFollowing" | "All";

/**
 * A repeat rule on the wire.
 *
 * 🔴 `anchorLocalDate` / `anchorLocalTime` / `timeZoneId` are a WALL CLOCK plus
 * a zone, NOT an instant. The server derives every occurrence from them with
 * `TimeZoneInfo` — deliberately not with `wallClockToUtcIso`, which resolves a
 * spring-forward gap an hour early and would then be wrong on every occurrence
 * rather than once. Do not "simplify" this to a UTC instant.
 *
 * `endOnLocalDate` is a wall-calendar `YYYY-MM-DD` (`calendarDateInputToIso`
 * territory) — never format it with the timezone-aware formatter.
 */
export interface RecurrenceRequestDto {
  frequency: RecurrenceFrequency;
  interval: number;
  endMode: RecurrenceEndMode;
  anchorLocalDate: string;
  anchorLocalTime: string;
  timeZoneId: string;
  byWeekday?: string | null;
  monthlyMode?: RecurrenceMonthlyMode | null;
  monthlyDayOfMonth?: number | null;
  monthlyWeekdayOrdinal?: number | null;
  monthlyWeekday?: string | null;
  endAfterOccurrences?: number | null;
  endOnLocalDate?: string | null;
}

/**
 * A series as a reader sees it. `description` always names an end date — a
 * series stops at the 24-month horizon and there is no top-up worker, so
 * "forever" would be a promise the platform does not keep.
 */
export interface RecurrenceSummaryDto {
  seriesId: string;
  description: string;
  rule: RecurrenceRequestDto;
  occurrenceCount: number;
  nextOccurrenceStartAt: string | null;
  /**
   * Future occurrences already edited on their own, which a series-wide edit
   * will not reach. Drives the scope dialog's "All" warning, which has to name
   * the number before the choice rather than after it.
   */
  detachedCount: number;
}

export interface AppointmentDto {
  id: string;
  ownerUserId: string;
  ownerDisplayName: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  status: AppointmentStatus;
  relatedEntityType: WorkTaskRelatedEntityType;
  relatedEntityId: string | null;
  relatedEntity: AppointmentRelatedEntitySummaryDto | null;
  reminderMinutesBefore: number | null;
  attendees: AppointmentAttendeeDto[];
  externalAttendees: ExternalAttendeeDto[];
  videoLink: string | null;
  dialInInstructions: string | null;
  isOwner: boolean;
  myResponse: AppointmentAttendeeResponse | null;
  createdAt: string;
  modifiedAt: string | null;
  /**
   * Set by the entity Calendar tab's read (`listByRelated`) on appointments the
   * viewer neither owns nor attends. On such a row the content fields are
   * blanked server-side — `title` is `""`, `description` / `location` /
   * `videoLink` / `dialInInstructions` / `reminderMinutesBefore` are null, and
   * both attendee lists are empty — while the time window, `status` and
   * `ownerDisplayName` are retained. Render "Busy" from local copy; never
   * derive a label from the empty title.
   *
   * Optional on the wire type so the existing inline test fixtures keep
   * compiling (the LoanSummaryDto convention); the server always sends it.
   */
  isRedacted?: boolean;
  /**
   * The organizer's choice to email internal attendees. Read back so the edit
   * form can hydrate the checkbox — without it the form posts `false` on every
   * save and silently switches the choice off.
   *
   * Optional on the wire type so the existing inline fixtures keep compiling
   * (the LoanSummaryDto convention); the server always sends it.
   */
  emailInternalAttendees?: boolean;
  /**
   * Set on every occurrence of a repeating appointment; null on a one-off.
   * Group by `seriesId ?? id` to collapse a series to one row.
   */
  seriesId?: string | null;
  /** The rule in words plus structured form. Present on every occurrence of a series. */
  recurrence?: RecurrenceSummaryDto | null;
  /** This occurrence was edited on its own and is exempt from later series-wide edits. */
  isDetached?: boolean;
  /**
   * The IANA zone this appointment's calendar DAY is written in — i.e. how to
   * READ `startAt` / `endAt`, not what to do about it. Null on a row created
   * before the server captured it, and for those the viewer's own zone is the
   * fallback, which is byte-for-byte what every surface did before this field
   * existed.
   *
   * 🔴 IT MATTERS ONLY WHEN `isAllDay` IS TRUE. A timed appointment is a real
   * instant and belongs in the viewer's zone. An all-day one is a wall-calendar
   * value stored as two instants — authored-zone midnight through 23:59 — so
   * reading its `startAt` in the viewer's zone lands a day EARLY for every
   * viewer west of the author, and its `endAt` a day LATE for every viewer east
   * of them. `isAllDay` alone cannot recover the authored day; this can.
   *
   * Do not branch on it by hand. `appointmentDayZone` in
   * `pages/calendar/lib/tz.ts` is the one place the rule is written, and every
   * surface that reads an appointment's day calls it.
   *
   * Optional on the wire type so the existing inline test fixtures keep
   * compiling (the LoanSummaryDto convention); the server always sends it.
   */
  authoredTimeZoneId?: string | null;
}

export interface CreateAppointmentDto {
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  relatedEntityType?: WorkTaskRelatedEntityType | null;
  relatedEntityId?: string | null;
  reminderMinutesBefore?: number | null;
  attendeeUserIds?: string[] | null;
  videoLink?: string | null;
  dialInInstructions?: string | null;
  externalAttendees?: ExternalAttendeeDto[] | null;
  /**
   * Present makes this a repeating appointment. The server REFUSES it alongside
   * `externalAttendees` — a series would otherwise send one invitation per
   * occurrence to a real external address.
   */
  recurrence?: RecurrenceRequestDto | null;
  /**
   * Email internal (staff) attendees the invitation too, on top of the in-app
   * notification they already get. Appointment-level: one choice for everybody.
   */
  emailInternalAttendees?: boolean;
}

/**
 * Partial update. Null members are "leave unchanged"; the explicit
 * <c>clear*</c> flags null out an optional field (FIND-12 discipline,
 * mirrors <c>UpdateWorkTaskDto</c>). <c>attendeeUserIds</c>, when non-null,
 * REPLACES the attendee set.
 */
export interface UpdateAppointmentDto {
  title?: string | null;
  description?: string | null;
  clearDescription?: boolean;
  location?: string | null;
  clearLocation?: boolean;
  startAt?: string | null;
  endAt?: string | null;
  isAllDay?: boolean | null;
  status?: AppointmentStatus | null;
  relatedEntityType?: WorkTaskRelatedEntityType | null;
  relatedEntityId?: string | null;
  clearRelatedEntity?: boolean;
  reminderMinutesBefore?: number | null;
  clearReminder?: boolean;
  attendeeUserIds?: string[] | null;
  videoLink?: string | null;
  clearVideoLink?: boolean;
  dialInInstructions?: string | null;
  clearDialInInstructions?: boolean;
  /**
   * When present, REPLACES the external attendee set (matched on email):
   * additions are emailed an invitation, removals a cancellation. Omitting it
   * leaves them unchanged — so a caller managing external attendees must send
   * the list even when it is empty, or removing the last one does nothing.
   */
  externalAttendees?: ExternalAttendeeDto[] | null;
  /**
   * Which occurrences this edit applies to. Ignored when the appointment has no
   * series, so a one-off save is unchanged and never prompts.
   */
  scope?: RecurrenceEditScope | null;
  /** Sets or replaces the repeat rule; on a one-off appointment this CONVERTS it into a series. */
  recurrence?: RecurrenceRequestDto | null;
  /** Drops the repeat rule, cancelling every future occurrence but this one. */
  clearRecurrence?: boolean;
  /**
   * Whether internal attendees get the invitation email. Omitting it leaves the
   * choice unchanged — it is NOT a `clear*`-style flag, and sending `false`
   * explicitly is how you switch it off.
   */
  emailInternalAttendees?: boolean | null;
}

export interface AppointmentListQuery {
  /** ISO instant — inclusive lower bound of the visible window. */
  from?: string;
  /** ISO instant — exclusive upper bound of the visible window. */
  to?: string;
  /** Honored only for callers holding <c>Appointments.ViewAll</c>. */
  ownerUserId?: string;
}

// --- Calendar AI Create+ — AI parse request/response shapes ---------------

/** One attendee as extracted from text, before internal/external classification. */
export interface ParsedAttendeeDto {
  name: string;
  email: string;
  notes?: string | null;
}

/** An extracted attendee resolved to an internal staff user. */
export interface ClassifiedAttendeeDto {
  userId: string;
  displayName: string;
  email: string;
}

/** A prospective-broker (Lead) draft extracted from the same text. */
export interface ParsedLeadDraft {
  firstName: string;
  lastName: string;
  companyName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  sourceDetail?: string | null;
  notes?: string | null;
}

/** Parse result: appointment fields + attendees split internal/external + optional Lead. */
/** A repeat phrase the parser recognised. No anchor — the form supplies that. */
export interface ParsedRecurrenceDto {
  frequency: RecurrenceFrequency;
  interval?: number;
  byWeekday?: string | null;
  monthlyMode?: RecurrenceMonthlyMode | null;
  monthlyDayOfMonth?: number | null;
}

export interface ParsedAppointmentWithClassificationDto {
  title: string;
  description: string | null;
  location: string | null;
  videoLink: string | null;
  dialInInstructions: string | null;
  startUtc: string | null;
  endUtc: string | null;
  reminderMinutesBefore: number;
  internalAttendees: ClassifiedAttendeeDto[];
  externalAttendees: ParsedAttendeeDto[];
  lead: ParsedLeadDraft | null;
  /** Appended, optional — see `ParsedAppointmentDto.Recurrence` server-side. */
  recurrence?: ParsedRecurrenceDto | null;
}

/** Body for `POST /appointments/recurrence-conflicts`. */
export interface RecurrenceConflictRequest {
  userIds: string[];
  recurrence: RecurrenceRequestDto;
  durationMinutes: number;
  excludeAppointmentId?: string | null;
  excludeSeriesId?: string | null;
}

/**
 * One busy attendee across the checked occurrences. Users with no busy
 * occurrence are OMITTED — a warning warns about problems, and that matches the
 * conflict badge's own presence-is-the-signal contract.
 *
 * `busyStartUtc` names the occurrences that clash; titles obey the same
 * `Appointments.ViewAll` gate as every other busy read and are null without it.
 */
export interface UserRecurrenceConflictDto {
  userId: string;
  occurrencesChecked: number;
  busyStartUtc: string[];
  titles: (string | null)[];
}

// --- Attendee conflict warning — informational, non-blocking overlap check ---

/** Body for POST /appointments/conflict-check. Mirrors the backend record. */
export interface AppointmentConflictCheckRequest {
  /** Organizer + every selected internal Team Member. */
  userIds: string[];
  startUtc: string;
  endUtc: string;
  /** The appointment being edited, so it never conflicts with itself. */
  excludeAppointmentId?: string | null;
}

/**
 * One appointment overlapping the proposed window for a checked user.
 * <c>title</c> is present ONLY when the caller holds Appointments.ViewAll
 * (busy-only otherwise — the server never sends a title it isn't allowed to).
 */
export interface ConflictingAppointmentDto {
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  title: string | null;
}

/** Per-user conflict result; only users with ≥1 overlap are returned. */
export interface UserConflictDto {
  userId: string;
  conflicts: ConflictingAppointmentDto[];
}

// --- Attendee availability strip — per-day busy/free timeline ----------------

/**
 * Body for POST /appointments/availability. Shape-identical to the conflict
 * check but a different question — "when is each person busy on this day?".
 * The window is the organizer's LOCAL calendar day as UTC instants; the 7am–7pm
 * strip is a display concern, so the full day is queried.
 */
export interface AppointmentAvailabilityRequest {
  /** Organizer + every selected internal Team Member. */
  userIds: string[];
  startUtc: string;
  endUtc: string;
  /** The appointment being edited, so it never shows as its own busy block. */
  excludeAppointmentId?: string | null;
}

/**
 * One block of busy time. <c>title</c> follows the same server-side gate as the
 * conflict check — present ONLY for Appointments.ViewAll holders. Free/busy
 * status itself is open to all staff; only appointment content is gated.
 */
export interface BusyBlockDto {
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  title: string | null;
}

/**
 * Per-user availability. Unlike UserConflictDto, EVERY requested user gets a
 * row — a free person returns an empty `busyBlocks` list rather than being
 * omitted, so the strip renders an explicit "free" row.
 */
export interface UserAvailabilityDto {
  userId: string;
  busyBlocks: BusyBlockDto[];
}

export const appointmentsApi = {
  list: (query?: AppointmentListQuery) =>
    api.get<AppointmentDto[]>("/appointments", query),

  /**
   * Every non-cancelled appointment linked to one entity — backs the Calendar
   * tab on entity detail pages. Route-shaped after
   * `workTasksApi.listByRelated` so the two entity surfaces read identically.
   *
   * Unlike `list`, this is NOT own-scope: appointments owned by other users are
   * returned so the tab can't invite double-booking by hiding them. Rows the
   * viewer neither owns nor attends arrive with `isRedacted` set and their
   * content blanked. Flat and unpaged — the panel's row caps are render
   * budgets, so "show more" pages through data already in hand.
   */
  listByRelated: (type: WorkTaskRelatedEntityType, entityId: string) =>
    api.get<AppointmentDto[]>(`/appointments/by-related/${type}/${entityId}`),

  /**
   * Informational, non-blocking scheduling-conflict check for the New/Edit
   * modal. Returns per-user overlapping appointments; never blocks save.
   */
  /**
   * Bounded busy check across the next few occurrences of a proposed rule.
   *
   * A THIRD endpoint on purpose. `conflictCheck` treats presence-in-the-response
   * as the conflict signal, so widening its window to a rule's whole span would
   * flag every appointment in it; `availability` answers a different question
   * over a single day. Both keep their contracts byte-identical.
   */
  recurrenceConflicts: (payload: RecurrenceConflictRequest) =>
    api.post<UserRecurrenceConflictDto[]>(
      "/appointments/recurrence-conflicts",
      payload,
    ),

  conflictCheck: (payload: AppointmentConflictCheckRequest) =>
    api.post<UserConflictDto[]>("/appointments/conflict-check", payload),

  /**
   * Per-user busy blocks for a day — backs the modal's attendee availability
   * strip. Separate from conflictCheck by design: that response's callers treat
   * presence-in-response as the conflict signal, so it can't carry a day-wide
   * window. Read-only; never gates create/save.
   */
  availability: (payload: AppointmentAvailabilityRequest) =>
    api.post<UserAvailabilityDto[]>("/appointments/availability", payload),

  /** Calendar AI Create+ — parse pasted text into a structured appointment draft. */
  aiParse: (rawText: string, timezone?: string) =>
    api.post<ParsedAppointmentWithClassificationDto>("/appointments/ai-parse", {
      rawText,
      timezone,
    }),

  upcoming: (limit = 5) =>
    api.get<AppointmentDto[]>("/appointments/upcoming", { limit }),

  get: (id: string) => api.get<AppointmentDto>(`/appointments/${id}`),

  create: (payload: CreateAppointmentDto) =>
    api.post<AppointmentDto>("/appointments", payload),

  update: (id: string, payload: UpdateAppointmentDto) =>
    api.patch<AppointmentDto>(`/appointments/${id}`, payload),

  /** Soft-cancels the appointment (sets status = Cancelled). */
  /**
   * Cancels an appointment. `scope` applies only to an occurrence of a series;
   * omitting it — which every pre-existing caller does — cancels exactly this
   * one row, the behaviour a one-off appointment has always had.
   */
  cancel: (id: string, scope?: RecurrenceEditScope) =>
    api.delete<void>(
      `/appointments/${id}${scope ? `?scope=${encodeURIComponent(scope)}` : ""}`,
    ),

  /**
   * Re-send the current invitation — to external attendees, and to internal
   * ones when `includeInternalAttendees` is set. `emails` narrows only the
   * EXTERNAL set; omitting it means all of them.
   *
   * Changes nothing about the meeting and does not advance its iTIP revision,
   * so a recipient who already has it sees no change.
   */
  resendInvitations: (
    id: string,
    emails?: string[],
    includeInternalAttendees = false,
  ) =>
    api.post<void>(`/appointments/${id}/resend-invitations`, {
      emails: emails && emails.length > 0 ? emails : null,
      includeInternalAttendees,
    }),

  addAttendees: (id: string, userIds: string[]) =>
    api.post<AppointmentDto>(`/appointments/${id}/attendees`, { userIds }),

  removeAttendee: (id: string, userId: string) =>
    api.delete<AppointmentDto>(`/appointments/${id}/attendees/${userId}`),

  respond: (id: string, response: "Accepted" | "Declined") =>
    api.post<AppointmentDto>(`/appointments/${id}/respond`, { response }),
};
